import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  ExpansionPanel,
  ExpansionPanelActionEvent,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import RippleButton from "../../components/common/RippleButton";
import ButtonForAll from "../../components/common/ButtonForAll";
import IconComponent from "../../components/common/IconComponent";
import { FaFilter } from "react-icons/fa";
import { Button } from "@progress/kendo-react-buttons";
import { useLocation } from "react-router-dom";
import { IFITBookingFilterDetails } from "./FITBookingModel";
import {
  clearFITBookingFilterDetails,
  setFITBookingFilterDetails,
} from "./FITBookingSlice";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import {
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";

const FITBookingFilter: React.FC = () => {
  const gridRef = useRef<any>(null);
  const BTNRef = useRef<any>(null);

  const dispatch = useAppDispatch();
  const location = useLocation();
  const QuotationID = location.state?.QuotationID;
  const loading = useAppSelector((state) => state.lead.loading);
  const [expand, setExpand] = useState("");
  const FITBookingFilterDetails = useAppSelector(
    (state) => state.FITBooking.FITBookingFilterDetails
  );

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [FITBookingFilterDetails]);

  useEffect(() => {
    const idMapping: Partial<Pick<IFITBookingFilterDetails, "QuotationID">> = {
      QuotationID,
    };

    const updatedFilterDetails = Object.entries(idMapping).reduce(
      (acc, [key, value]) => {
        const typedKey = key as keyof IFITBookingFilterDetails;

        return {
          ...acc,
          [typedKey]: FITBookingFilterDetails?.[typedKey]
            ? `${FITBookingFilterDetails[typedKey]}`
            : value
            ? +value
            : null,
        };
      },
      {} as Partial<IFITBookingFilterDetails>
    );

    if (
      Object.values(updatedFilterDetails).some((value) => value) &&
      JSON.stringify(updatedFilterDetails) !==
        JSON.stringify(FITBookingFilterDetails)
    ) {
      dispatch(
        setFITBookingFilterDetails({
          ...FITBookingFilterDetails,
          ...updatedFilterDetails,
        })
      );
    }
  }, []);

  const handleSubmit = async (values: any) => {
    const insertPayload = {
      QuotationID: FITBookingFilterDetails?.QuotationID || QuotationID || null,
      TourBookingNo: values?.TourBookingNo ? values?.TourBookingNo : "",
      BookingDate: values?.BookingDate
        ? moment(values?.BookingDate).format("YYYY-MM-DD")
        : "",
      FirstName: values?.FirstName ? values?.FirstName : "",
      MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
      EmailID: values?.EmailID ? values?.EmailID : "",
      SectorName: values?.SectorName ? values?.SectorName : "",
      filterDateType: values?.filterDateType ? +values?.filterDateType : null,
      filterFromDate: values?.filterFromDate
        ? moment(values?.filterFromDate)?.format("YYYY-MM-DD")
        : "",
      filterToDate: values?.filterToDate
        ? moment(values?.filterToDate)?.format("YYYY-MM-DD")
        : "",
    };
    setExpand("");
    dispatch(setFITBookingFilterDetails(insertPayload));
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}

      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={{
          ...FITBookingFilterDetails,
          BookingDate: FITBookingFilterDetails?.BookingDate
            ? moment(
                FITBookingFilterDetails?.BookingDate,
                "YYYY-MM-DD"
              ).toDate()
            : "",
          filterFromDate: FITBookingFilterDetails?.filterFromDate
            ? moment(
                FITBookingFilterDetails?.filterFromDate,
                "YYYY-MM-DD"
              ).toDate()
            : "",
          filterToDate: FITBookingFilterDetails?.filterToDate
            ? moment(
                FITBookingFilterDetails?.filterToDate,
                "YYYY-MM-DD"
              ).toDate()
            : "",
        }}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ExpansionPanel
              className="inquiry-filter-panel m-0"
              title={
                <div>
                  <div>
                    <IconComponent icon={FaFilter} /> Filter Results
                  </div>
                </div>
              }
              expanded={expand === "filter"}
              tabIndex={1}
              onAction={(event: ExpansionPanelActionEvent) => {
                const nativeEvent = event.nativeEvent as KeyboardEvent;
                const ActiveBTN = document.activeElement as HTMLElement;

                if (
                  nativeEvent.type === "keydown" &&
                  nativeEvent.code === "Space"
                ) {
                  nativeEvent.preventDefault();
                  return;
                } else if (
                  nativeEvent.code === "Enter" ||
                  nativeEvent.code === "NumpadEnter"
                ) {
                  nativeEvent.preventDefault();
                  if (ActiveBTN.id === "FITBookingSubmitBtn") {
                    BTNRef.current.element.click();
                  } else if (ActiveBTN.id === "ClearFITBookingDetail") {
                    setExpand("");
                  }
                  return;
                }

                if (event?.expanded) {
                  setExpand("");
                } else {
                  setExpand("filter");
                }
              }}
            >
              {expand === "filter" && (
                <GridLayout
                  style={{ marginRight: 20, padding: 10 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem>
                    <Field
                      name="TourBookingNo"
                      label="Tour Booking No."
                      placeholder="Tour Booking No."
                      maxLength="100"
                      component={FormTextField}
                      onKeyDown={(e: any) => {
                        if (e.key === " ") {
                          formRenderProps.onChange("TourBookingNo", {
                            value: `${e?.target?.value} `,
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  {/* <GridLayoutItem>
                    <Field
                      name="BookingDate"
                      label="Booking Date"
                      format="dd/MM/yyyy"
                      component={FormDatePicker}
                    />
                  </GridLayoutItem> */}
                  <GridLayoutItem>
                    <Field
                      name="SectorName"
                      label="Sector"
                      placeholder="Sector"
                      maxLength="100"
                      component={FormTextField}
                      onKeyDown={(e: any) => {
                        if (e.key === " ") {
                          formRenderProps.onChange("SectorName", {
                            value: `${e?.target?.value} `,
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="FirstName"
                      label="First Name"
                      placeholder="First Name"
                      maxLength="100"
                      component={FormTextField}
                      onKeyDown={(e: any) => {
                        if (e.key === " ") {
                          formRenderProps.onChange("FirstName", {
                            value: `${e?.target?.value} `,
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="MobileNo"
                      label="Mobile No"
                      type="number"
                      placeholder="i.e. 9999999999"
                      max={10}
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="EmailID"
                      label="Email ID"
                      placeholder="i.e. example@gmail.com"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="w-100"
                      id={"filterDateType"}
                      name={"filterDateType"}
                      label={"Date Type"}
                      placeholder="Date Type"
                      component={FormSelectionField}
                      options={[
                        { value: 1, label: "Tour Date" },
                        { value: 2, label: "Booking Date" },
                      ]}
                      onChange={(e: any) => {
                        if (!e?.value) {
                          formRenderProps.onChange("filterFromDate", {
                            value: "",
                          });
                          formRenderProps.onChange("filterToDate", {
                            value: "",
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  {formRenderProps.valueGetter("filterDateType") && (
                    <>
                      <GridLayoutItem>
                        <Field
                          name={"filterFromDate"}
                          label="From Date"
                          format="dd/MM/yyyy"
                          component={FormDatePicker}
                          validator={
                            formRenderProps.valueGetter("filterDateType")
                              ? requiredValidator
                              : skipValidator
                          }
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          name={"filterToDate"}
                          label="To Date"
                          format="dd/MM/yyyy"
                          disabled={
                            !formRenderProps.valueGetter("filterFromDate")
                          }
                          validator={
                            formRenderProps.valueGetter("filterFromDate")
                              ? requiredValidator
                              : skipValidator
                          }
                          component={FormDatePicker}
                          minDate={moment(
                            formRenderProps.valueGetter("filterFromDate")
                          ).toDate()}
                        />
                      </GridLayoutItem>
                    </>
                  )}
                  <GridLayoutItem
                    colSpan={
                      formRenderProps.valueGetter("filterDateType") ? 1 : 3
                    }
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                      marginTop: 20,
                    }}
                  >
                    <RippleButton
                      id={"ClearContactDetail"}
                      type="button"
                      fillMode="outline"
                      themeColor="error"
                      style={{ marginRight: 10 }}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        dispatch(clearFITBookingFilterDetails());
                      }}
                    >
                      Clear
                    </RippleButton>
                    <ButtonForAll
                      id={"FITBookingSubmitBtn"}
                      label={"Filter"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                    />

                    <RippleButton
                      id={"ClearFITBookingDetail"}
                      type="button"
                      fillMode="outline"
                      themeColor="primary"
                      style={{ marginLeft: 10 }}
                      onClick={() => setExpand("")}
                    >
                      Cancel
                    </RippleButton>
                  </GridLayoutItem>
                </GridLayout>
              )}
              {
                <Button
                  style={{ opacity: 0, position: "absolute", left: -50 }}
                  ref={BTNRef}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || loading}
                >
                  Filter
                </Button>
              }
            </ExpansionPanel>
          </FormElement>
        )}
      />
    </>
  );
};

export default FITBookingFilter;
